import type { ComponentProps } from "react";
import React, { lazy, Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Typography, useI18nContext } from "@procore/core-react";
import { reportError } from "@/utils/logging-utils";
import type { EquipmentSelectField as EagerEquipmentSelectField } from "./equipment-select-field";

type EquipmentSelectFieldProps = ComponentProps<typeof EagerEquipmentSelectField>;

// Import the CommonJS re-export, not the package. An import() from this ES module
// would resolve the package's "import" build and bundle a second copy of shared
// packages such as @procore/toast-alert, whose React context the app's provider
// wouldn't reach.
const LazyEquipmentSelectField = lazy(() =>
   import("./equipment-select-field").then((module) => ({ default: module.EquipmentSelectField })),
);

const LoadError = ({
   label,
   colStart = 1,
   colWidth = 6,
}: Pick<EquipmentSelectFieldProps, "label" | "colStart" | "colWidth">) => {
   const I18n = useI18nContext();
   return (
      <div style={{ gridColumnStart: colStart, gridColumnEnd: colStart + colWidth }}>
         <Typography as="p" intent="label">
            {label}
         </Typography>
         <Typography as="p" color="red50">
            {I18n.t("views.company.workforce_planning.equipment_unavailable")}
         </Typography>
      </div>
   );
};

// Keeps a failed chunk download from reaching the page's error boundary, which
// would unmount the page and discard the rest of the form.
export const EquipmentSelectField = (props: EquipmentSelectFieldProps) => (
   <ErrorBoundary
      fallback={
         <LoadError label={props.label} colStart={props.colStart} colWidth={props.colWidth} />
      }
      onError={(error) => reportError(error, "Equipment field failed to load")}
   >
      <Suspense fallback={null}>
         <LazyEquipmentSelectField {...props} />
      </Suspense>
   </ErrorBoundary>
);
